// IndexedDB layer (spec §8.2/§8.3). Stores:
//   tickets — registrationId -> { ticketNumber, status, checkedInLocally } — the
//             device's offline source of truth, downloaded at the "prepare
//             offline" step. Indexed by ticket number so manual entry is a
//             lookup, not a scan over every registration.
//   outbox  — queued scan events; client-generated UUID ids make retried sync
//             pushes naturally idempotent (§8.4). Drained by feature 009.
//   meta    — event public key, device auth token, last sync time.
import { openDB } from 'idb';

const DB_NAME = 'kontriticket-checkin';
const DB_VERSION = 1;

let dbPromise;

export function openDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        db.createObjectStore('meta');
        const tickets = db.createObjectStore('tickets', { keyPath: 'registrationId' });
        tickets.createIndex('by-number', 'ticketNumber', { unique: false });
        db.createObjectStore('outbox', { keyPath: 'id' });
      },
    });
  }
  return dbPromise;
}

export async function getMeta(key) {
  return (await openDb()).get('meta', key);
}

export async function setMeta(entries) {
  const db = await openDb();
  const tx = db.transaction('meta', 'readwrite');
  await Promise.all([
    ...Object.entries(entries).map(([k, v]) => tx.store.put(v, k)),
    tx.done,
  ]);
}

export async function clearAll() {
  const db = await openDb();
  const tx = db.transaction(['meta', 'tickets', 'outbox'], 'readwrite');
  await Promise.all([
    tx.objectStore('meta').clear(),
    tx.objectStore('tickets').clear(),
    tx.objectStore('outbox').clear(),
    tx.done,
  ]);
}

/**
 * Replace the ticket set with a freshly downloaded one.
 *
 * `checkedInLocally` is carried across deliberately. It records what THIS
 * device admitted, which is not the same fact as the server's `status` and is
 * not represented anywhere else — until feature 009 flushes the outbox, the
 * server has never heard about it. Dropping it on refresh would make every
 * delegate this device already admitted scan clean a second time.
 */
export async function replaceTickets(tickets) {
  const db = await openDb();
  // Read, clear and repopulate in ONE transaction. Reading through a separate
  // transaction would let a concurrent markCheckedIn land between the read and
  // the clear, and that admission would be erased — the exact loss this
  // carry-over exists to prevent.
  const tx = db.transaction('tickets', 'readwrite');
  const existing = await tx.store.getAll();
  const localState = new Map(
    existing.map((t) => [t.registrationId, { checkedInLocally: t.checkedInLocally, checkedInAt: t.checkedInAt }]),
  );

  await tx.store.clear();
  for (const t of tickets) {
    const local = localState.get(t.registrationId);
    tx.store.put({
      registrationId: t.registrationId,
      ticketNumber: t.ticketNumber,
      status: t.status,
      checkedInLocally: local?.checkedInLocally ?? false,
      checkedInAt: local?.checkedInAt ?? null,
    });
  }
  await tx.done;
}

export async function getTicket(registrationId) {
  return (await openDb()).get('tickets', registrationId);
}

export async function getTicketByNumber(ticketNumber) {
  return (await openDb()).getFromIndex('tickets', 'by-number', ticketNumber);
}

export async function countTickets() {
  return (await openDb()).count('tickets');
}

export async function markCheckedIn(registrationId, at) {
  const db = await openDb();
  const existing = await db.get('tickets', registrationId);
  if (!existing) return;
  await db.put('tickets', { ...existing, checkedInLocally: true, checkedInAt: at });
}

/**
 * Append a scan to the outbox (§8.4).
 *
 * The id is generated HERE, on the device, which is what makes a retried push
 * idempotent: the same scan sent twice over a flaky connection carries the same
 * id and is one row, not two. Feature 009 drains it, and relies on that id.
 *
 * ⚠️  `mode` defaults to 'offline' and the CALLER overrides it when the verdict
 * came from the server (feature 009). It is not decoration: `mode` is what makes
 * `duplicate_rejected` mean anything on the conflict feed — caught at the door
 * (online, prevented) versus discovered at reconciliation (offline, both people
 * are already inside). Defaulting to 'offline' is the safe direction: claiming a
 * scan was arbitrated online when it was not would tell the organizer a
 * duplicate was prevented when nobody stopped anyone.
 */
export async function queueScan(entry) {
  const db = await openDb();
  await db.put('outbox', { id: crypto.randomUUID(), mode: 'offline', ...entry });
}

/** Everything waiting to be pushed (feature 009). */
export async function listOutbox() {
  return (await openDb()).getAll('outbox');
}

/**
 * Remove only what the server confirmed it holds (feature 009).
 *
 * ⚠️  Never `clear()`. A push returns per-scan outcomes precisely so a scan the
 * server could not accept STAYS in the queue and stays visible in the queued
 * count, rather than being silently dropped because its neighbours succeeded.
 */
export async function clearOutbox(ids) {
  if (!ids?.length) return;
  const db = await openDb();
  const tx = db.transaction('outbox', 'readwrite');
  await Promise.all([...ids.map((id) => tx.store.delete(id)), tx.done]);
}

/**
 * Apply a sync delta to the local ticket store (feature 009).
 *
 * ⚠️  Upsert, never replace. `replaceTickets` wipes the store, which is right
 * for a full download and catastrophic for a delta — every ticket not mentioned
 * would vanish, and a missing ticket reads as "unknown", which §8.3 says to
 * ADMIT. A delta must only ever add and update.
 */
export async function applyDelta(rows) {
  if (!rows?.length) return 0;
  const db = await openDb();
  const tx = db.transaction('tickets', 'readwrite');
  for (const row of rows) {
    const existing = await tx.store.get(row.registrationId);
    await tx.store.put({ ...(existing ?? {}), ...row });
  }
  await tx.done;
  return rows.length;
}

export async function countOutbox() {
  return (await openDb()).count('outbox');
}
